import { Box, Center, Divider, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

import ButtonedRadioGroup from '../../../components/ButtonedRadioGroup';
import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { Label } from '../../../components/formComponents';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { useTemporaryInfoUpdateMutation } from '../../../gql/generated/graphql';
import { useStep } from '../../../hooks';
import { useTempInfo } from '../../../hooks/useTempInfo';
import {
  trackMarketplaceTimelineSelected,
  trackMarketplaceTradeInSelected,
} from '../../../services/analytics/marketplaceEvents';

const TRADE_IN_OPTIONS = [
  { label: "Yes, I'm open to trade-in offers", value: 'open_to_trade_in' },
  { label: 'No, I just want to sell', value: 'just_sell' },
  { label: 'Not sure yet', value: 'not_sure' },
];

const TIMELINE_OPTIONS = [
  { label: 'As soon as possible', value: 'asap' },
  { label: 'In the next couple of weeks', value: 'couple_weeks' },
  { label: 'I have a month or more', value: 'month_or_more' },
];

const MarketplaceQuestions = () => {
  const { updateState, isExpanded } = useLEAccordionItemContext();
  const { info, infoLoading } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const step = useStep();

  const handleSubmit = async (values: { trade_in_preference: string; sell_timeline: string }) => {
    updateState({ status: 'loading' });

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            trade_in_preference: values.trade_in_preference,
            sell_timeline: values.sell_timeline,
          },
        },
      },
    });

    if (info?.id) {
      trackMarketplaceTradeInSelected(
        info.id,
        values.trade_in_preference as 'open_to_trade_in' | 'just_sell' | 'not_sure',
      );
      trackMarketplaceTimelineSelected(info.id, values.sell_timeline);
    }

    updateState({ status: 'complete' });
    step.moveNext();
  };

  if (infoLoading || !info?.data) {
    return null;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        trade_in_preference: info?.data?.trade_in_preference ?? 'open_to_trade_in',
        sell_timeline: info?.data?.sell_timeline ?? 'asap',
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Form>
          <Box>
            <Label>OPEN TO TRADING IN AT A DEALERSHIP?</Label>
            <ButtonedRadioGroup
              columns={{ base: 1, md: 3 }}
              options={TRADE_IN_OPTIONS}
              value={values.trade_in_preference}
              name="trade_in_preference"
              onChange={(val: string) => setFieldValue('trade_in_preference', val)}
            />

            <Divider mt={8} mb={8} />

            <Label>HOW SOON DO YOU NEED TO SELL?</Label>
            <ButtonedRadioGroup
              columns={{ base: 1, md: 3 }}
              options={TIMELINE_OPTIONS}
              value={values.sell_timeline}
              name="sell_timeline"
              onChange={(val: string) => setFieldValue('sell_timeline', val)}
            />
            <Text fontSize="sm" color="gray.500" textAlign="center" mt={4}>
              No pressure — we will work on your timeline.
            </Text>

            <Center>
              <PrimaryButton
                mt="30px"
                mb={{ base: '20px', lg: '0' }}
                loading={isSubmitting}
                type="submit"
                value="BUILD MY LISTING"
              />
            </Center>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default MarketplaceQuestions;
